import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Trash2, X, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useSlip } from "@/context/BetSlipContext";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { api, errMsg } from "@/lib/api";

const BOOSTS = [[10, 0.3], [8, 0.2], [5, 0.1], [3, 0.05]];
const boostFor = (n) => (BOOSTS.find(([l]) => n >= l) || [0, 0])[1];

const SlipItem = ({ s, onRemove }) => (
  <div data-testid={`betslip-item-${s.event.id}`} className="rounded-md border border-[#1E293B] bg-[#0E131F] p-3">
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <div className="truncate text-sm font-semibold text-white">
          {s.pick === "draw" ? "Draw" : s.pick === "home" ? s.event.home : s.event.away}
        </div>
        <div className="truncate text-xs text-slate-400">{s.event.home} v {s.event.away}</div>
        <div className="text-[10px] uppercase tracking-wider text-slate-500">
          {s.event.state === "in" ? "Live · " : ""}Match result · {s.event.league}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className={`font-odds text-sm font-bold ${s.changed ? "text-[#F59E0B]" : "text-[#10B981]"}`}>{s.odds.toFixed(2)}</span>
        <button data-testid={`betslip-remove-${s.event.id}`} onClick={onRemove} className="text-slate-500 hover:text-white">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  </div>
);

export const BetSlip = () => {
  const { items, remove, clear, updateOdds } = useSlip();
  const { user, setAuthOpen, refresh } = useAuth();
  const { currency, fmt, rate } = useCurrency();
  const qc = useQueryClient();
  const [mode, setMode] = useState("single");
  const [stake, setStake] = useState("");
  const [busy, setBusy] = useState(false);
  const effMode = items.length < 2 ? "single" : mode;
  const st = parseFloat(stake) || 0;
  const totalOdds = items.reduce((a, s) => a * s.odds, 1);
  const boost = effMode === "multi" ? boostFor(items.length) : 0;
  const stakeUsd = st / rate;
  const potentialUsd =
    effMode === "multi"
      ? stakeUsd + stakeUsd * (totalOdds - 1) * (1 + boost)
      : items.reduce((a, s) => a + stakeUsd * s.odds, 0);
  const totalStake = effMode === "multi" ? st : st * items.length;

  const place = async () => {
    if (!user) return setAuthOpen(true);
    setBusy(true);
    try {
      await api.post("/bets", {
        mode: effMode,
        selections: items.map((s) => ({ event_id: s.event.id, pick: s.pick, odds: s.odds })),
        stake: st,
        currency,
      });
      toast.success("Bet placed! Good luck.");
      clear();
      setStake("");
      refresh();
      qc.invalidateQueries({ queryKey: ["bets"] });
    } catch (e) {
      if (e.response?.status === 409) {
        updateOdds(e.response.data.detail.changes);
        toast.warning("Odds changed — review and place again.");
      } else toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="betslip" className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-[#1E293B] py-3 pl-4 pr-12 xl:pr-4">
        <h2 className="font-display text-lg font-black uppercase tracking-tight">
          Bet Slip <span data-testid="betslip-count" className="ml-1 rounded bg-[#F59E0B] px-1.5 text-sm text-[#0B0E14]">{items.length}</span>
        </h2>
        {items.length > 0 && (
          <button data-testid="betslip-clear" onClick={clear} className="flex items-center gap-1 text-xs text-slate-400 hover:text-white">
            <Trash2 className="h-3.5 w-3.5" /> Clear
          </button>
        )}
      </div>
      {items.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 p-8 text-center text-sm text-slate-400">
          <Zap className="h-8 w-8 text-[#1E293B]" />
          Tap any odds to add a selection. Add 3+ to a multi for a winnings boost.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-1 p-3">
            {["single", "multi"].map((m) => (
              <button
                key={m}
                data-testid={`betslip-tab-${m}`}
                disabled={m === "multi" && items.length < 2}
                onClick={() => setMode(m)}
                className={`rounded-md py-1.5 text-xs font-bold uppercase tracking-wider transition-colors disabled:opacity-40 ${
                  effMode === m ? "bg-[#10B981] text-[#0B0E14]" : "bg-[#1A2234] text-slate-300"
                }`}
              >
                {m === "single" ? `Singles (${items.length})` : "Multi"}
              </button>
            ))}
          </div>
          <div className="scroll-thin flex-1 space-y-2 overflow-y-auto px-3">
            {items.map((s) => (
              <SlipItem key={s.event.id} s={s} onRemove={() => remove(s.event.id)} />
            ))}
          </div>
          <div className="space-y-3 border-t border-[#1E293B] p-4">
            {effMode === "multi" && (
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Total odds</span>
                <span data-testid="betslip-total-odds" className="font-odds font-bold">{totalOdds.toFixed(2)}</span>
              </div>
            )}
            {effMode === "multi" && boost > 0 && (
              <div data-testid="betslip-boost" className="flex justify-between rounded bg-[#10B981]/10 px-2 py-1 text-xs font-semibold text-[#10B981]">
                <span>Multi boost</span> <span>+{Math.round(boost * 100)}% winnings</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-400">{effMode === "single" ? "Stake each" : "Stake"} ({currency})</span>
              <Input
                data-testid="betslip-stake-input"
                type="number"
                min="0"
                inputMode="decimal"
                value={stake}
                onChange={(e) => setStake(e.target.value)}
                className="h-9 bg-[#0E131F] text-right font-odds"
                placeholder="0.00"
              />
            </div>
            <div className="grid grid-cols-4 gap-1">
              {[10, 50, 100, 500].map((v) => (
                <button
                  key={v}
                  data-testid={`betslip-quick-stake-${v}`}
                  onClick={() => setStake(String(v))}
                  className="rounded bg-[#1A2234] py-1 text-xs font-semibold hover:bg-[#242F46]"
                >
                  {v}
                </button>
              ))}
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-400">Potential win</span>
              <span data-testid="betslip-potential-win" className="font-odds font-bold text-[#10B981]">{fmt(potentialUsd)}</span>
            </div>
            <Button
              data-testid="betslip-place-bet-button"
              disabled={busy || (user && st <= 0)}
              onClick={place}
              className="h-11 w-full bg-[#10B981] font-display text-base font-black uppercase tracking-wide text-[#0B0E14] hover:bg-[#059669]"
            >
              {!user ? "Log in to bet" : busy ? "Placing…" : `Place bet · ${totalStake.toFixed(2)} ${currency}`}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
